import * as React from 'react';
import {useSelector} from 'react-redux';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ListRow} from 'shells/list-row/list-row';
import {ListRowWithIcon} from 'shells/list-row/list-row-with-icon';
import {ListViewScrollable} from 'shells/list-view/list-view-scrollable';

import type {NutshellState} from '../../../store';

import {getFormSchemaFields} from '../../forms/forms-selectors';

import {fieldSortFunction, filterListItems, getTitle, getSubtitle, getIcon} from './helpers';
import type {FormSchemaProperty} from './types';

type Props = {
    onSelectField: (field: FormSchemaProperty) => void;
    customFilterFunction: (fields: FormSchemaProperty[]) => any[];
    getIsItemDisabled?: (field: FormSchemaProperty) => boolean;
    getDisabledText?: (field: FormSchemaProperty) => string;
    searchValue: string;
    customSpecialStates?: any[];
};

export function NutshellFieldPicker(props: Props) {
    const schemaFields = useSelector((state: NutshellState) => getFormSchemaFields(state));

    const fields = props.customFilterFunction(schemaFields).sort(fieldSortFunction);

    return (
        <ListViewScrollable
            collection={filterListItems(fields, props.searchValue)}
            renderRow={(field: FormSchemaProperty, isSelected: boolean) => {
                const isItemDisabled = props.getIsItemDisabled
                    ? props.getIsItemDisabled(field)
                    : false;

                return (
                    <ListRow
                        key={getKey(fields, field)}
                        onClick={isItemDisabled ? undefined : () => props.onSelectField(field)}
                        isSelected={isSelected}
                    >
                        <ListRowWithIcon
                            title={getTitle(field)}
                            subtitle={getSubtitle(
                                field.entityType,
                                field.isCustomField,
                                field.displayType
                            )}
                            icon={getIcon(field.entityType)}
                            isDisabled={isItemDisabled}
                            disabledSubtitleText={
                                isItemDisabled && props.getDisabledText
                                    ? props.getDisabledText(field)
                                    : undefined
                            }
                        />
                    </ListRow>
                );
            }}
            onSelect={(field: FormSchemaProperty) => props.onSelectField(field)}
            specialStates={
                props.customSpecialStates || [
                    {
                        shouldRender: () => filterListItems(fields, props.searchValue).length === 0,
                        component: (
                            <NothingFoundEmptyState
                                title='No fields were found'
                                subtitle='Try loosening your search!'
                            />
                        ),
                    },
                ]
            }
        />
    );
}

const getKey = (fields: FormSchemaProperty[], item: FormSchemaProperty) => {
    return fields.findIndex(
        (field) => field.entityType === item.entityType && field.name === item.name
    );
};
